import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { OverlayModule } from '@angular/cdk/overlay';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { OverlayService } from 'shared/services/overlay.service';
import { AvatarComponent } from 'shared/components/avatar/avatar.component';
import { UserStore } from 'features/user/store/user.store';
import { IUserProfile } from 'features/user/api/api-tokens';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserAuthService } from 'features/user/services/user-auth.service';
import { UserService } from 'features/user/services/user.service';
import { NotificationBellComponent } from '../../../notifications/components/notification-bell/notification-bell.component';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { environment } from 'environments/environment';
import { NotificationService } from 'app/shared/services/notification.service';
import { MaintenanceStore } from 'core/stores/maintenance.store';
import { PermissionService } from 'core/services/permission.service';
import { BillingAccessService } from 'features/billing/billing-access.service';
import { DashboardKey, isDashboardAvailable } from 'features/user/dashboard-defaults';

@Component({
  selector: 'app-header',
  standalone: true,
  templateUrl: './header.component.html',
  imports: [
    CommonModule,
    RouterLink,
    OverlayModule,
    MatButton,
    MatIcon,
    AvatarComponent,
    HasPermissionDirective,
    NotificationBellComponent,
  ],
})
export class HeaderComponent {
  isOpen = false;
  userStore = inject(UserStore);
  private maintenanceStore = inject(MaintenanceStore);
  private permissionService = inject(PermissionService);
  /** Billing link: Freely or FRB active profile AND BILLING_VIEW (or system admin). */
  canViewBilling = inject(BillingAccessService).canView;
  userName = signal<string>('');

  /**
   * The current environment name, but only in non-production environments
   * (empty string in prod / before the public /systems/health check resolves).
   * Shown in italics beside the "accept" logo in the header.
   */
  nonProdEnv = computed(() => {
    const env = this.maintenanceStore.snapshot()?.environment;
    return !!env && env !== 'unknown' && !/prod/i.test(env) ? env : '';
  });
  constructor(
    public router: Router,
    protected authService: UserAuthService,
    protected overlayService: OverlayService,
    protected userService: UserService,
    protected notificationService: NotificationService,
    protected destroyRef?: DestroyRef
  ) {

    this.userService.getUserData().subscribe(data => {
      this.userName.set(data.userInfo.firstName + ' ' + data.userInfo.lastName);
    });
  }

  isSystemAdmin = computed(() => {
    const userInfo = this.userStore.userInfo();
    const isAdmin = userInfo.userInfo?.userSettings?.isSystemAdmin || false;
    if (isAdmin) return true;
    const roles = this.userStore.activeProfile()?.roles || [];
    return roles.includes('SYSTEM_ADMIN');
  });

  /**
   * Whether the current user can access the ERP dashboard nav link.
   * Mirrors backend permissions: only system admins or members of
   * GROUP_ERP_Administrator. The feature flag `enableERPDashboard` is
   * checked separately in the template.
   */
  canAccessErpDashboard = computed(() => {
    if (this.isSystemAdmin()) return true;
    const roles = this.userStore.activeProfile()?.roles || [];
    return roles.includes('GROUP_ERP_Administrator');
  });

  /**
   * Is this dashboard on the Dashboard sub-nav? The same rule `/dashboard`'s redirect uses to
   * honour the profile's Default Dashboard, so the default is always one the menu offers.
   */
  dashboardAvailable(key: DashboardKey): boolean {
    const detail = this.userStore.activeProfile()?.profileDetail;
    return isDashboardAvailable(key, {
      settings: detail?.profileAdminSettings,
      erpEnabled: !!detail?.erpEnabled,
      canAccessErp: this.canAccessErpDashboard(),
      canReadLedger: this.permissionService.hasPermission('GET_TRANSACTION_STATS'),
    });
  }

  /** Quick Pay is an Integrated Payment Mode feature: it needs invoicing on as well as its own flag. */
  quickPayEnabled = computed(() => {
    const s: any = this.userStore.activeProfile()?.profileDetail?.profileAdminSettings;
    return !!s?.enableInvoice && !!s?.enableQuickPay;
  });

  isSystemProfile = computed(() => {
    const profileId = this.userStore.activeProfile()?.profileDetail?.profileId?.toLowerCase();
    return profileId === 'freely';
  });

  get profiles(): IUserProfile[] {
    const sorted = this.userStore.profiles()
      .filter(p => !p.profileDetail?.isDisabled)
      .sort((a, b) =>
        (a.profileDetail?.profileName ?? '').localeCompare(
          b.profileDetail?.profileName ?? '',
          undefined,
          { sensitivity: 'base' }
        )
      );
    const freelyIdx = sorted.findIndex(
      p => p.profileDetail?.profileId?.toLowerCase() === 'freely'
    );
    if (freelyIdx > 0) {
      const [freely] = sorted.splice(freelyIdx, 1);
      sorted.unshift(freely);
    }
    return sorted;
  }

  get activeProfile(): IUserProfile | undefined {
    return this.userStore.activeProfile();
  }

  selectProfile(profile: IUserProfile ) {
    this.userStore.setActiveProfile(profile.profileDetail.profileId);
      const currentUrl = this.router.url.split('?')[0];
      const queryParams = this.router.parseUrl(this.router.url).queryParams;
      console.log('Current URL:', currentUrl);
      console.log('Query Params:', queryParams);
      this.router.navigate(['/dashboard']).then(() => {
        window.location.reload();
      });

   // window.location.reload();
  
  }

  logout() {
    //this.overlayService.showOverlay();
    this.authService.logout().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  /**
   * agent-desktop SSO (browser -> app). Mint a one-time exchange link for the current user + active
   * profile, then launch the installed desktop app via its custom scheme. If nothing handles the
   * scheme within ~1.5s (app not installed), fall back to the download page.
   * Visibility is gated by *hasPermission="'AGENT_DESKTOP_LOGIN'" in the template.
   */
  startDesktopAgent() {
    this.isOpen = false;
    const profileId = this.activeProfile?.profileDetail?.profileId;
    if (!profileId) {
      this.notificationService.error('No active profile is selected.');
      return;
    }

    this.notificationService.success('Launching the Freely Desktop Agent…');

    this.authService.mintDesktopAgentLink(profileId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: ({ link }) => {
        const cfg = environment.desktopAgent;

        // If the OS hands the scheme to the installed app, the browser tab loses focus -> blur.
        // If we're still focused after a moment, the app isn't installed/registered.
        let launched = false;
        const onBlur = () => { launched = true; };
        window.addEventListener('blur', onBlur, { once: true });

        window.location.href = `${cfg.scheme}://exchange?link=${encodeURIComponent(link)}`;

        setTimeout(() => {
          window.removeEventListener('blur', onBlur);
          if (!launched && !document.hidden) {
            // App not installed / scheme unhandled — send them to the in-app download page. Router
            // navigation is reliable (a background window.open() would be popup-blocked here).
            this.notificationService.error('Freely Desktop Agent was not detected on this machine.');
            this.router.navigate(['/download-desktop-agent']);
          }
        }, 2000);
      },
      error: () => {
        this.notificationService.error('Could not start the desktop agent. Please try again.');
      }
    });
  }

  isCurrentRoute(path: string): boolean {
    return this.router.url.split('?')[0] === path;
  }

  /** Current path without query params. */
  private currentPath(): string {
    return this.router.url.split('?')[0];
  }

  /**
   * True when the current route is within the Dashboard section (dashboard or any
   * of its sub-pages). Drives the always-visible horizontal sub-nav shown beneath
   * the "Dashboard" nav item while you're inside it.
   */
  isDashboardSection(): boolean {
    const p = this.currentPath();
    return p === '/dashboard' || p.startsWith('/dashboard/');
  }

  /**
   * True when the current route is within the History section (payments,
   * transactions, requests). Drives the horizontal sub-nav under "History".
   */
  isHistorySection(): boolean {
    const p = this.currentPath();
    return p === '/history' || p.startsWith('/history/') || p.startsWith('/requests');
  }

  /**
   * The nav section currently being hovered (or null). Hovering a top item with
   * sub-pages temporarily shows ITS sub-nav (in the same horizontal format) and
   * hides the active section's — so only one sub-nav is ever visible.
   */
  hoveredSection = signal<string | null>(null);

  /** Show the Dashboard sub-nav: the hovered section wins, else the active section. */
  showDashboardSubnav(): boolean {
    const h = this.hoveredSection();
    return h ? h === 'dashboard' : this.isDashboardSection();
  }

  /** Show the History sub-nav: the hovered section wins, else the active section. */
  showHistorySubnav(): boolean {
    const h = this.hoveredSection();
    return h ? h === 'history' : this.isHistorySection();
  }

  navigateTo(path: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation(); // Stop event propagation to prevent dropdown issues
    console.log(`Navigating to ${path}`);
    this.router.navigate([path]);
  }
}